var _excluded = ["onDotPress", "onNextPress", "onPreviousPress", "onSlideChange", "color", "children"],
    _excluded2 = ["infinite", "slidesToShow", "autoPlay", "pauseOnInteraction", "autoPlaySpeed", "fade", "transitionSpeed", "children", "lazyLoad"];

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import { forwardRef, styled, useIsMobile } from '@grupoboticario/flora-react';
import React, { Children, createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import { CustomButtom } from './aside-navigation/NavigationButton';
import { Dot } from './bottom-navigation/NavigationDot';
import { DEFAULT_CONTEXT_VALUE } from './CarouselContext';
var StyledCarouselContext = styled('div', {
  position: 'relative',
  ["" + (CustomButtom == null ? void 0 : CustomButtom.toString())]: {
    transform: 'translate(0, -93%)',
    '@mobile': {
      transform: 'translate(0, -75%)'
    }
  },
  variants: {
    variant: {
      container: {
        padding: '0 calc($6 + $2)',
        '@mobile': {
          padding: '0 calc($10 + $2)'
        },
        ["" + (CustomButtom == null ? void 0 : CustomButtom.toString())]: {
          '&[data-to=previous]': {
            left: 0
          },
          '&[data-to=next]': {
            right: 0
          }
        }
      },
      fluid: {
        ["" + (CustomButtom == null ? void 0 : CustomButtom.toString())]: {
          '&[data-to=previous]': {
            left: '$2'
          },
          '&[data-to=next]': {
            right: '$2'
          }
        }
      }
    }
  },
  defaultVariants: {
    variant: 'container'
  }
});
export var CarouselContext = /*#__PURE__*/createContext(DEFAULT_CONTEXT_VALUE);
export var Carousel = _ref => {
  var {
    onDotPress,
    onNextPress,
    onPreviousPress,
    onSlideChange,
    color = 'standard',
    children
  } = _ref,
      props = _objectWithoutPropertiesLoose(_ref, _excluded);

  var [state, setState] = useState(DEFAULT_CONTEXT_VALUE.state);
  var handleOnDotPress = useCallback(index => {
    onDotPress == null ? void 0 : onDotPress(index);
  }, [onDotPress]);
  var handleOnNextPress = useCallback(() => {
    onNextPress == null ? void 0 : onNextPress();
  }, [onNextPress]);
  var handleOnPreviousPress = useCallback(() => {
    onPreviousPress == null ? void 0 : onPreviousPress();
  }, [onPreviousPress]);
  var handleOnSlideChange = useCallback(index => {
    onSlideChange == null ? void 0 : onSlideChange(index);
  }, [onSlideChange]);
  useEffect(() => {
    setState(prevState => _extends({}, prevState, {
      onDotPress: handleOnDotPress,
      onNextPress: handleOnNextPress,
      onPreviousPress: handleOnPreviousPress,
      onSlideChange: handleOnSlideChange,
      color
    }));
  }, [handleOnDotPress, handleOnNextPress, handleOnPreviousPress, handleOnSlideChange, color]);
  return /*#__PURE__*/React.createElement(CarouselContext.Provider, {
    value: {
      state,
      setState
    }
  }, /*#__PURE__*/React.createElement(StyledCarouselContext, props, children));
}; // ------------------------------------ //

var MAX_SLIDES_TO_SHOW_DESKTOP = 6;
var MAX_SLIDES_TO_SHOW_MOBILE = 4;
var StyledCarouselContent = styled('div', {
  width: '100%',
  '.slick-list': {
    minHeight: '$14'
  }
});
var StyledList = styled('ul', {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '$4'
});
export var CarouselContent = forwardRef((_ref2, forwardedRef) => {
  var {
    infinite = true,
    slidesToShow: slidesToShowProps = 1,
    autoPlay = false,
    pauseOnInteraction = true,
    autoPlaySpeed = 5000,
    fade = false,
    transitionSpeed = 250,
    children,
    lazyLoad
  } = _ref2,
      props = _objectWithoutPropertiesLoose(_ref2, _excluded2);

  var {
    setState: setContext,
    state: {
      onSlideChange: onSlideChangeContext,
      color
    }
  } = useContext(CarouselContext);
  var [currentIndex, setCurrentIndex] = useState(0);
  var sliderRef = useRef(null);
  var isMobile = useIsMobile();

  var onSlideChange = index => {
    var currentIndexValue = Math.ceil(index / slidesToShow);
    onSlideChangeContext(currentIndexValue);
    setCurrentIndex(currentIndexValue);
    setContext(prevContext => _extends({}, prevContext, {
      currentIndex: currentIndexValue
    }));
  };

  var slidesToShow = useMemo(() => {
    if (slidesToShowProps > MAX_SLIDES_TO_SHOW_MOBILE && isMobile) {
      return MAX_SLIDES_TO_SHOW_MOBILE;
    }

    return slidesToShowProps > MAX_SLIDES_TO_SHOW_DESKTOP ? MAX_SLIDES_TO_SHOW_DESKTOP : slidesToShowProps;
  }, [isMobile, slidesToShowProps]);
  var sliderSettings = {
    accessibility: true,
    afterChange: onSlideChange,
    arrows: true,
    autoplay: autoPlay,
    autoplaySpeed: autoPlaySpeed,
    dots: true,
    infinite,
    lazyLoad,
    nextArrow: /*#__PURE__*/React.createElement("div", null),
    pauseOnDotsHover: pauseOnInteraction,
    pauseOnFocus: pauseOnInteraction,
    pauseOnHover: pauseOnInteraction,
    prevArrow: /*#__PURE__*/React.createElement("div", null),
    appendDots: dots => {
      return /*#__PURE__*/React.createElement(StyledList, null, /*#__PURE__*/React.createElement(React.Fragment, null, dots));
    },
    customPaging: index => {
      return /*#__PURE__*/React.createElement(Dot, {
        index: index,
        "aria-label": "Exibir Slide " + (index + 1),
        active: currentIndex === index,
        color: color
      });
    },
    slidesToShow,
    slidesToScroll: slidesToShow,
    fade,
    swipe: true,
    speed: transitionSpeed
  };
  useEffect(() => {
    if (sliderRef && sliderRef.current) {
      var {
        current: sliderCurrentRef
      } = sliderRef;
      setContext(prevContext => _extends({}, prevContext, {
        goTo: index => {
          sliderCurrentRef.slickGoTo(index);
        },
        goToNext: () => {
          sliderCurrentRef.slickNext();
        },
        goToPrevious: () => {
          sliderCurrentRef.slickPrev();
        }
      }));
    }
  }, [sliderRef, setContext, currentIndex, slidesToShow]);
  useEffect(() => {
    setContext(prevContext => _extends({}, prevContext, {
      pages: Math.ceil(Children.count(children) / slidesToShow),
      infinite: infinite
    }));
  }, [children, setContext, slidesToShow, infinite]);
  return /*#__PURE__*/React.createElement(StyledCarouselContent, _extends({}, props, {
    ref: forwardedRef
  }), /*#__PURE__*/React.createElement(Slider, _extends({
    ref: sliderRef
  }, sliderSettings), children));
});
//# sourceMappingURL=Carousel.js.map